import type { PathWatcherEvent } from '@webcontainer/api';
import type { File, FileMap } from '.';
import { walkFileMap } from './walk';
import { IGNORE_BLOB_ID } from '~/lib/constants';
import { isBinaryFile } from '~/utils/is-binary-file';
import { removeTrailingSlashes } from '~/utils/path';

/**
 * Applies file updates to the given file map based on the provided events.
 *
 * It mutates the provided file map.
 *
 * @param files File map to be updated
 * @param events Events to be applied
 * @param handleBinaryFile Callback to handle binary files
 */
export function applyFileUpdates(
  files: FileMap,
  events: Array<[events: PathWatcherEvent[]]>,
  handleBinaryFile: (path: string, buffer: Uint8Array) => void,
) {
  const watchEvents = [];
  const changed = new Set<string>();

  /**
   * Dedupe 'change' / 'add_file' events.
   *
   * When we call `writeFile` of the WebContainer API, this internally does:
   *
   *  1. open(<path>, "w", 0o666) -> this truncates the file and triggers a 'change' with an empty buffer
   *  2. write(<path>, data) -> this actually adds the new data
   *
   * There might be additional write calls if the file size is greater than 524288.
   *
   * Because watchPath triggers for each of those actions we get duplicate events except that
   * the correct file content is only present on the last event.
   */
  const allEvents = events.flat(2);
  const seenFileChange = new Set<string>();

  for (const event of allEvents.reverse()) {
    const { type, path } = event;

    if (type === 'change' || type === 'add_file' || type === 'remove_file') {
      if (seenFileChange.has(path)) {
        continue;
      }

      seenFileChange.add(path);
      watchEvents.push(event);
    } else {
      watchEvents.push(event);
    }
  }

  // put them back in order
  watchEvents.reverse();

  let sizeDelta = 0;

  for (const { type, path, buffer } of watchEvents) {
    const sanitizedPath = removeTrailingSlashes(path);

    switch (type) {
      case 'add_dir': {
        files[sanitizedPath] = { type: 'folder', isLocked: false, isTargeted: false };
        break;
      }
      case 'remove_dir': {
        for (const { path } of walkFileMap(files, sanitizedPath)) {
          delete files[path];
        }

        break;
      }
      case 'add_file':
      case 'change': {
        let content = '';

        /**
         * @note This check is purely for the editor. The way we detect this is not
         * bullet-proof and it's a best guess so there might be false-positives.
         * The reason we do this is because we don't want to display binary files
         * in the editor nor allow to edit them.
         */
        const isBinary = isBinaryFile(buffer);

        let previousVersion: File | undefined;

        if (files[sanitizedPath]) {
          previousVersion = files[sanitizedPath] as File;
        }

        if (!previousVersion) {
          sizeDelta++;
        }

        /**
         * If the previous version of this file is a fake binary file, it means that
         * we actually expect the content to not be a binary file, but rather the URL where the
         * file can be downloaded.
         *
         * However, since the actual content gets written into WebContainer after downloading,
         * the content would become a binary file. This makes the `isBinary` check
         * return `true`, when we don't want it to. We want to keep the URL
         * unless it actually changes.
         *
         * Since we are only storing that information in the `FileMap`, we need to
         * copy over the URL from the previous version into the new version's content.
         *
         * In the classic editor, we don't run into this issue as the watcher does not
         * get added until all files are written.
         *
         * This code is not perfect because if the file actually gets overwritten with new
         * content, we would not know it. Changing this behavior probably requires a larger refactor,
         * which we don't have time for right now.
         */

        if (previousVersion?.isStackBlitzBinary) {
          // copy over the download url
          content = previousVersion.content;
        } else if (isBinary) {
          handleBinaryFile(sanitizedPath, buffer as Uint8Array);

          // create a dummy entry so it appears in the filetree while we upload
          files[sanitizedPath] = {
            type: 'file',
            content: '',
            isBinary: true,
            isStackBlitzBinary: true,
            blobId: IGNORE_BLOB_ID,
            isLocked: previousVersion?.isLocked ?? false,
            isTargeted: previousVersion?.isTargeted ?? false,
            lastModified: Date.now(),
          };

          /**
           * @note Previously, we did not include binary files in the set of changed files.
           * However, I believe it is now safe to do so. This will ensure that the file map
           * immediately reflects the changes and includes the new file.
           */
          break;
        } else if (!isBinary) {
          content = decodeFileContent(buffer);
        }

        // avoid changing the atom if nothing has changed
        if (content === previousVersion?.content) {
          continue;
        }

        files[sanitizedPath] = {
          type: 'file',
          content,
          isBinary: previousVersion?.isBinary ?? isBinary,
          isStackBlitzBinary: previousVersion?.isStackBlitzBinary ?? false,
          blobId: previousVersion?.blobId,
          isLocked: previousVersion?.isLocked ?? false,
          isTargeted: previousVersion?.isTargeted ?? false,
          lastModified: Date.now(),
        };

        break;
      }
      case 'remove_file': {
        sizeDelta--;
        delete files[sanitizedPath];
        break;
      }
      case 'update_directory': {
        // we don't care about these events
        break;
      }
    }

    changed.add(path);
  }

  return {
    changed,
    sizeDelta,
    files,
  };
}

const utf8TextDecoder = new TextDecoder('utf8', { fatal: true });

export function decodeFileContent(buffer?: Uint8Array) {
  if (!buffer || buffer.byteLength === 0) {
    return '';
  }

  try {
    return utf8TextDecoder.decode(buffer);
  } catch (error) {
    console.info(error);
    return '';
  }
}
